import { getThemeColors } from './theme-colors';

// Validates HEX color format: #RGB, #RRGGBB, or #RRGGBBAA
export const isValidHexColor = (color: string): boolean =>
  /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6}|[A-Fa-f0-9]{8})$/.test(color);

export const hasNoColor = (color: string | undefined): boolean =>
  color === '' || color === undefined;

export const ensureHexPrefix = (color: string): string =>
  hasNoColor(color) ? '' : `#${color.replace(/^#+/g, '')}`;

// Strip alpha and expand #RGB → #RRGGBB, returning a 7-char hex.
export const toRgbHex = (hex: string): string => {
  if (hex.length === 4) {
    return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
  }
  return hex.length >= 7 ? hex.slice(0, 7) : hex;
};

// Decode the alpha byte of an 8-char hex (#RRGGBBAA) as 0–100. Defaults to 100.
export const alphaToOpacity = (hex: string): number =>
  hex.length === 9 ? Math.round((parseInt(hex.slice(7, 9), 16) / 255) * 100) : 100;

// Combine an RGB hex + opacity (0–100) into an 8-char hex. Returns the input unchanged when opacity is 100.
export const applyOpacity = (rgbHex: string, opacity: number): string => {
  if (!isValidHexColor(rgbHex) || opacity >= 100) {
    return rgbHex;
  }
  const full = toRgbHex(rgbHex);
  const alpha = Math.round((opacity / 100) * 255);
  return `${full}${alpha.toString(16).padStart(2, '0')}`;
};

// Interpolate between two hex colors (6- or 8-char) at position t ∈ [0, 1].
export const interpolateHex = (c1: string, c2: string, t: number): string => {
  const r1 = parseInt(c1.slice(1, 3), 16);
  const g1 = parseInt(c1.slice(3, 5), 16);
  const b1 = parseInt(c1.slice(5, 7), 16);
  const a1 = c1.length === 9 ? parseInt(c1.slice(7, 9), 16) : 255;
  const r2 = parseInt(c2.slice(1, 3), 16);
  const g2 = parseInt(c2.slice(3, 5), 16);
  const b2 = parseInt(c2.slice(5, 7), 16);
  const a2 = c2.length === 9 ? parseInt(c2.slice(7, 9), 16) : 255;
  const r = Math.round(r1 + (r2 - r1) * t);
  const g = Math.round(g1 + (g2 - g1) * t);
  const b = Math.round(b1 + (b2 - b1) * t);
  const a = Math.round(a1 + (a2 - a1) * t);
  const hex = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  return a < 255 ? `${hex}${a.toString(16).padStart(2, '0')}` : hex;
};

// WCAG 2.x relative luminance: gamma-expand each sRGB channel to linear light,
// then weight by the Rec.709 coefficients (R 0.2126, G 0.7152, B 0.0722 — they
// sum to 1.0 and reflect the human eye's sensitivity to each primary).
// See https://www.w3.org/WAI/GL/wiki/Relative_luminance
const getLuminance = (color: string): number => {
  const r = parseInt(color.slice(1, 3), 16) / 255;
  const g = parseInt(color.slice(3, 5), 16) / 255;
  const b = parseInt(color.slice(5, 7), 16) / 255;
  const toLinear = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
};

// WCAG contrast ratio: (L_lighter + 0.05) / (L_darker + 0.05). The 0.05 is
// an ambient-flare offset that models reflected light on the display so the
// ratio stays finite (and bounded at 21:1) even for pure black.
const getContrastRatio = (a: string, b: string): number => {
  const aL = getLuminance(a);
  const bL = getLuminance(b);
  return aL > bL ? (aL + 0.05) / (bL + 0.05) : (bL + 0.05) / (aL + 0.05);
};

export const getAutoTextColor = (bgColor: string | undefined): string => {
  const theme = getThemeColors();
  if (hasNoColor(bgColor)) {
    return theme.textDefault;
  }
  // For #RRGGBBAA below ~50% alpha, the background isn't opaque enough to drive
  // contrast — keep the default text color instead.
  if (bgColor?.length === 9 && parseInt(bgColor.slice(7, 9), 16) < 128) {
    return theme.textDefault;
  }
  return getContrastRatio(bgColor ?? '', theme.neutral0) >=
    getContrastRatio(bgColor ?? '', theme.neutral100)
    ? theme.neutral0
    : theme.neutral100;
};
